import { t } from "@lingui/macro";
import { useState } from "react";

import { Button, Input } from "@whoosh/design-system/src/components/controls";
import { CouponCodeIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

type Props = {
  code: string;
  errorMessage: string | null;
  isApplyingPromoCode: boolean;
  onApply: () => void;
  onCancel: () => void;
  onCodeChange: (value: string) => void;
};

export const ExpandedPromoCodeState = ({
  code,
  errorMessage,
  isApplyingPromoCode,
  onApply,
  onCancel,
  onCodeChange,
}: Props) => {
  const [applyButtonWidth, setApplyButtonWidth] = useState<number>();

  return (
    <VStack gap={3} testID="booking-promo-code">
      <HStack alignItems="center" justifyContent="space-between" width="100%">
        <HStack alignItems="center" gap={3} flex={1}>
          <Box
            width={32}
            height={20}
            borderRadius="0.5"
            backgroundColor="surface-main-primary-subtlest"
            alignItems="center"
            justifyContent="center"
          >
            <CouponCodeIcon color="icon-main-primary" width={16} height={16} />
          </Box>
          <Text variant="mainBaseSemiBold" color="text-main-primary">
            {t`Apply Promo Code`}
          </Text>
        </HStack>
        <Pressable
          accessibilityLabel={t`Cancel promo code`}
          accessibilityRole="button"
          testID="cancel-coupon-code-button"
          borderRadius="0.5"
          paddingHorizontal={2}
          paddingVertical={1}
          onPress={onCancel}
          _hover={{ opacity: 0.7 }}
          _pressed={{ opacity: 0.5 }}
        >
          <Text variant="mainSmallSemiBold" color="text-secondary">
            {t`Cancel`}
          </Text>
        </Pressable>
      </HStack>

      <HStack gap={2} alignItems="center">
        <Box flex={1}>
          <Input
            name="coupon-code-input"
            accessibilityLabel={t`Promo code`}
            testID="coupon-code-input"
            placeholder={t`Enter promo code...`}
            value={code}
            type="text"
            onChange={(value) => onCodeChange(value.toString())}
          />
        </Box>
        <Button
          accessibilityLabel={t`Apply promo code`}
          size="small"
          isLoading={isApplyingPromoCode}
          width={isApplyingPromoCode ? applyButtonWidth : undefined}
          onLayout={(event) => {
            if (!isApplyingPromoCode) {
              setApplyButtonWidth(event.nativeEvent.layout.width);
            }
          }}
          onPress={onApply}
        >
          {t`Apply`}
        </Button>
      </HStack>

      {errorMessage ? (
        <Text variant="mainTiny" color="text-error" testID="coupon-code-error">
          {errorMessage}
        </Text>
      ) : null}
    </VStack>
  );
};
